import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { dashboardAPI } from "../../../../Api/dashboardAPI";
import { DashboardInitState } from "../../Slices/dashboardSlice";

type ServiceVersionResponse = {
  version?: string;
  environment?: string;
};

type ServiceDescriptor = {
  name: string;
  category: string;
  getVersion: () => Promise<ServiceVersionResponse>;
};

export type ServiceVersionRow = {
  name: string;
  version: string;
  environment: string;
  category: string;
  status: "loading" | "ok" | "error";
};

const REPLICA_COUNT = 10;

// Tamaño de lote para los pings de /version. Mantiene la concurrencia acotada
// para no saturar el navegador ni los servicios cuando hay 50+ workers.
const VERSION_BATCH_SIZE = 10;

const buildReplicaServices = (
  baseName: string,
  category: string,
  getVersion: (workerNumber: number) => Promise<ServiceVersionResponse>
): ServiceDescriptor[] =>
  Array.from({ length: REPLICA_COUNT }, (_, index) => ({
    name: `${baseName} ${index}`,
    category,
    getVersion: () => getVersion(index),
  }));

const principalServices: ServiceDescriptor[] = [
  {
    name: "Backend Notifications",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackNotification(),
  },
  {
    name: "Backend Proxy",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackProxy(),
  },
  {
    name: "Backend Sync",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackSync(null),
  },
  {
    name: "Backend API",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackApi(null),
  },
  {
    name: "Backend TX",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackTx(),
  },
  {
    name: "Backend Eventos",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackEventos(),
  },
  {
    name: "Backend Oferta",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackOferta(),
  },
  {
    name: "Backend Cupos",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackCupos(),
  },
  {
    name: "Backend Novedades",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackNovedades(),
  },
  {
    name: "Backend Validaciones",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackValidaciones(),
  },
  {
    name: "Backend Update Cupos",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackUpdateCupos(),
  },
  {
    name: "Backend Dashboard",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackDashboard(),
  },
  {
    name: "Backend Login",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackLogin(),
  },
  {
    name: "Backend Fijadas",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackFijadas(),
  },
  {
    name: "Backend Inscriptas",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackInscriptas(),
  },
  {
    name: "Backend Update Oferta",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackUpdateOferta(),
  },
  {
    name: "Backend Prioridad",
    category: "Principal",
    getVersion: () => dashboardAPI.getVersionBackPrioridad(),
  },
];

const services: ServiceDescriptor[] = [
  ...principalServices,
  ...buildReplicaServices(
    "Backend Inscripciones",
    "Múltiples Inscripciones",
    (n) => dashboardAPI.getVersionBackMultipleInscripciones(n)
  ),
  ...buildReplicaServices(
    "Backend Validaciones",
    "Múltiples Validaciones",
    (n) => dashboardAPI.getVersionBackMultipleValidaciones(n)
  ),
  ...buildReplicaServices("Backend Cupos", "Múltiples Cupos", (n) =>
    dashboardAPI.getVersionBackMultipleCupos(n)
  ),
  ...buildReplicaServices("Backend Oferta", "Múltiples Oferta", (n) =>
    dashboardAPI.getVersionBackMultipleOferta(n)
  ),
];

// Acciones incrementales: el slice las consume en `getServicesVersionsBuilder`.
// Definidas con createAction (en este archivo) para evitar una dependencia
// circular slice <-> thunk que romper\u00eda el bundle.
export const setServicesVersionsLoading = createAction<ServiceVersionRow[]>(
  "dashboard/setServicesVersionsLoading"
);
export const updateServiceVersion = createAction<ServiceVersionRow>(
  "dashboard/updateServiceVersion"
);

const buildLoadingRow = (service: ServiceDescriptor): ServiceVersionRow => ({
  name: service.name,
  version: "",
  environment: "",
  category: service.category,
  status: "loading",
});

export const getServicesVersions = createAsyncThunk(
  "dashboard/getServicesVersions",
  async (_, thunkAPI) => {
    const startedAt = performance.now();

    // 1. Pre-cargamos la lista en estado "loading" para que la UI pinte
    //    todas las cards inmediatamente con un skeleton, sin pantalla en blanco.
    thunkAPI.dispatch(setServicesVersionsLoading(services.map(buildLoadingRow)));

    // 2. Resolvemos en lotes paralelos y actualizamos cada card a medida
    //    que su /version responde. La UI se siente fluida porque cada
    //    servicio aparece individualmente en cuanto su request termina.
    for (let start = 0; start < services.length; start += VERSION_BATCH_SIZE) {
      const batch = services.slice(start, start + VERSION_BATCH_SIZE);
      await Promise.allSettled(
        batch.map(async (service) => {
          try {
            const res = await service.getVersion();
            thunkAPI.dispatch(
              updateServiceVersion({
                name: service.name,
                version: res?.version ?? "-",
                environment: res?.environment ?? "-",
                category: service.category,
                status: "ok",
              })
            );
          } catch (error) {
            thunkAPI.dispatch(
              updateServiceVersion({
                name: service.name,
                version: "-",
                environment: "-",
                category: service.category,
                status: "error",
              })
            );
          }
        })
      );
    }

    const elapsedMs = Math.round(performance.now() - startedAt);
    console.log(
      `getServicesVersions: ${services.length} servicios consultados en ${elapsedMs}ms`
    );
    return services.length;
  }
);

export const getServicesVersionsBuilder = (
  builder: ActionReducerMapBuilder<DashboardInitState>
) => {
  builder
    .addCase(setServicesVersionsLoading, (state, action) => {
      state.servicesVersions = action.payload;
    })
    .addCase(updateServiceVersion, (state, action) => {
      const idx = state.servicesVersions.findIndex(
        (s) => s.name === action.payload.name
      );
      if (idx !== -1) {
        state.servicesVersions[idx] = action.payload;
      }
    });
};
